import { TimeLineEvent, TimelineSpan, TimelineSpanParent } from '../domain/TimelineSpan'
import { TraceTick } from '../domain/Trace'

interface TickDependency {
    id: string
    cs: number
    dependencies: string[]
}

class SpanService {
    public sort = (spans: TimelineSpan[]): TimelineSpan[] => {
        if (spans.length === 0) {
            return []
        }

        const sortedSpans = spans.sort((a: TimelineSpan, b: TimelineSpan) => {
            // Spans that started earlier should be sorted earlier.
            const startTime = Math.sign(a.cs - b.cs)

            if (startTime !== 0) {
                return startTime
            }

            // If two spans start at the same time, the longer span should come first.
            const duration = Math.sign(a.wt - b.wt)

            return -duration
        })

        for (let i = 0; i < sortedSpans.length; ++i) {
            for (let j = i - 1; j >= 0; --j) {
                if (
                    this.contains(sortedSpans[j], sortedSpans[i]) &&
                    (!this.isRepeatSpan(sortedSpans[j]) || (this.isRepeatSpan(sortedSpans[i]) && this.isRepeatSpan(sortedSpans[j])))
                ) {
                    sortedSpans[i].parent = sortedSpans[j].id
                    sortedSpans[i].parentTick = sortedSpans[j].tick
                    break
                }
            }
        }

        const tickDependencies: { [tickId: string]: TickDependency } = {}
        for (const span of sortedSpans) {
            if (!span.tick) {
                continue
            }
            if (!tickDependencies[span.tick]) {
                tickDependencies[span.tick] = {
                    id: span.tick,
                    cs: Math.pow(2, 25) - 1,
                    dependencies: [],
                }
            }

            if (span.parent) {
                if (span.parentTick) {
                    tickDependencies[span.tick].dependencies.push(span.parentTick)
                }
            }
            tickDependencies[span.tick].cs = Math.min(tickDependencies[span.tick].cs, span.cs)
        }

        const tickList = Object.values(tickDependencies).sort((a, b) => Math.sign(a.cs - b.cs))
        for (const tick of tickList) {
            tick.dependencies = tick.dependencies.filter((value, index, sourceArray) => sourceArray.indexOf(value) === index)
        }

        const firstTick = tickList.shift()

        if (!firstTick) {
            return []
        }

        const sortedTicks: TickDependency[] = [firstTick]
        const collectedTicks = [firstTick.id]

        tickLoop: while (tickList.length) {
            for (let i = 0; i < tickList.length; ++i) {
                // Tick for which all dependencies already have been collected
                if (tickList[i].dependencies.filter(element => !collectedTicks.includes(element)).length === 0) {
                    const ticks = tickList.splice(i, 1)[0]
                    collectedTicks.push(ticks.id)
                    sortedTicks.push(ticks)
                    continue tickLoop
                }
            }

            // If we did not find a tick to add we just add the first one so
            // that we get a deterministic loop
            const tick = tickList.shift()
            if (!tick) {
                break
            }
            collectedTicks.push(tick.id)
            sortedTicks.push(tick)
        }

        return sortedTicks.flatMap(tick => sortedSpans.filter(e => e.tick === tick.id))
    }

    public getParents = (spans: TimelineSpan[], span: TimelineSpan): TimelineSpanParent[] => {
        const parents: TimelineSpanParent[] = []

        while (span.parent) {
            const parent = spans.find(element => element.id === span.parent)

            if (!parent) {
                return parents
            }

            if (parent.async) {
                span = parent
                continue
            }

            if (parent.e && parent.e.length) {
                for (const e of parent.e) {
                    const event = e

                    if (this.contains(event, span)) {
                        event.span = parent
                        parents.push({ span: event.span, event })
                    }
                }
            }

            parents.push({ span: parent })
            span = parent
        }

        return parents
    }

    // Keep only ticks referenced by the current spans, preserving the first-seen span order.
    public filterTicks = <Tick extends TraceTick>(ticks: { [id: string]: Tick }, spans: TimelineSpan[]): Tick[] => {
        const tickIds = spans
            .map(span => span.tick)
            .filter((tickId): tickId is string => Boolean(tickId))
            .filter((value, index, sourceArray) => sourceArray.indexOf(value) === index)

        return Object.values(this.pick(ticks, tickIds))
    }

    private pick = <T>(object: { [id: string]: T }, keys: string[]): { [id: string]: T } =>
        keys.reduce<{ [id: string]: T }>((result, key) => {
            if (Object.prototype.hasOwnProperty.call(object, key)) {
                result[key] = object[key]
            }
            return result
        }, {})

    private contains = (a: TimelineSpan | TimeLineEvent, b: TimelineSpan | TimeLineEvent): boolean =>
        b.cs >= a.cs && b.cs + b.wt <= a.cs + a.wt

    private isRepeatSpan = (span: TimelineSpan) => span.annotations && 'repeat_count' in span.annotations
}

export default SpanService
